import { useState } from 'react';
import { Filter, X, Check, Clock, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { LeaveApprovalFilters } from '@/types/leaveApprovalFilters';
import { 
  LEAVE_REQUEST_STATUSES, 
  generateMonthOptions,
  SPECIAL_MONTH_VALUES,
} from '@/types/leaveApprovalFilters';

interface Employee {
  id: string;
  first_name: string | null;
  last_name: string | null;
}

interface LeaveApprovalFilterPopoverProps {
  filters: LeaveApprovalFilters;
  onUpdateFilter: <K extends keyof LeaveApprovalFilters>(
    key: K,
    value: LeaveApprovalFilters[K]
  ) => void;
  onApply: () => void;
  onClear: () => void;
  employees: Employee[];
  availableLeaveTypes: string[];
  activeFilterCount: number;
  hasPendingChanges: boolean;
}

// Helper to format leave type for display
const formatLeaveType = (type: string): string => {
  return type
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
};

export const LeaveApprovalFilterPopover = ({
  filters,
  onUpdateFilter,
  onApply,
  onClear,
  employees,
  availableLeaveTypes,
  activeFilterCount,
  hasPendingChanges,
}: LeaveApprovalFilterPopoverProps) => {
  const [open, setOpen] = useState(false);
  const monthOptions = generateMonthOptions();

  // Toggle employee selection
  const toggleEmployee = (employeeId: string) => {
    const current = filters.employeeIds;
    const updated = current.includes(employeeId)
      ? current.filter(id => id !== employeeId)
      : [...current, employeeId];
    onUpdateFilter('employeeIds', updated);
  };

  // Toggle leave type selection
  const toggleLeaveType = (type: string) => {
    const current = filters.leaveTypes;
    const updated = current.includes(type)
      ? current.filter(t => t !== type)
      : [...current, type];
    onUpdateFilter('leaveTypes', updated);
  };

  // Toggle status selection
  const toggleStatus = (status: string) => {
    const current = filters.statuses;
    const updated = current.includes(status)
      ? current.filter(s => s !== status)
      : [...current, status];
    onUpdateFilter('statuses', updated);
  };

  const handleApply = () => {
    onApply();
    setOpen(false);
  };

  const handleClear = () => {
    onClear();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Filter className="h-4 w-4" />
          Filters
          {activeFilterCount > 0 && (
            <Badge variant="secondary" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
              {activeFilterCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[420px] p-0 bg-popover" align="start">
        <div className="p-3 border-b">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-sm">Filter Leave Requests</h4>
            {activeFilterCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClear}
                className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
              >
                <X className="h-3 w-3 mr-1" />
                Clear all
              </Button>
            )}
          </div>
        </div>

        <div className="p-3">
          <div className="grid grid-cols-2 gap-3">
            {/* Month Filter */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Time Period</Label>
              <Select
                value={filters.month || ''}
                onValueChange={(value) => onUpdateFilter('month', value)}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Select period" />
                </SelectTrigger>
                <SelectContent>
                  {/* Quick Filters */}
                  <SelectGroup>
                    <SelectLabel className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Quick Filters
                    </SelectLabel>
                    {monthOptions
                      .filter(opt => opt.group === 'quick')
                      .map((option) => (
                        <SelectItem key={option.value} value={option.value} className="text-xs">
                          {option.label}
                        </SelectItem>
                      ))}
                  </SelectGroup>
                  
                  <Separator className="my-1" />
                  
                  {/* Future Months */}
                  {monthOptions.some(opt => opt.group === 'future') && (
                    <SelectGroup>
                      <SelectLabel className="text-xs text-muted-foreground flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        Upcoming
                      </SelectLabel>
                      {monthOptions
                        .filter(opt => opt.group === 'future')
                        .map((option) => (
                          <SelectItem key={option.value} value={option.value} className="text-xs">
                            {option.label}
                          </SelectItem>
                        ))}
                    </SelectGroup>
                  )}
                  
                  {/* Current & Past Months */}
                  <SelectGroup>
                    <SelectLabel className="text-xs text-muted-foreground">
                      {monthOptions.some(opt => opt.group === 'future') ? 'Current & Past' : 'Months'}
                    </SelectLabel>
                    {monthOptions
                      .filter(opt => opt.group === 'current' || opt.group === 'past')
                      .map((option) => (
                        <SelectItem key={option.value} value={option.value} className="text-xs">
                          {option.label}
                          {option.group === 'current' && (
                            <span className="ml-1 text-muted-foreground">(current)</span>
                          )}
                        </SelectItem>
                      ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            {/* Status Filter */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">
                Status
                {filters.statuses.length > 0 && (
                  <span className="text-muted-foreground ml-1">
                    ({filters.statuses.length})
                  </span>
                )}
              </Label>
              <div className="grid grid-cols-2 gap-1">
                {LEAVE_REQUEST_STATUSES.map((status) => (
                  <div key={status.value} className="flex items-center space-x-1.5">
                    <Checkbox
                      id={`status-${status.value}`}
                      checked={filters.statuses.includes(status.value)}
                      onCheckedChange={() => toggleStatus(status.value)}
                      className="h-3.5 w-3.5"
                    />
                    <label
                      htmlFor={`status-${status.value}`}
                      className="text-xs cursor-pointer"
                    >
                      {status.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Employee Filter */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">
                Employees
                {filters.employeeIds.length > 0 && (
                  <span className="text-muted-foreground ml-1">
                    ({filters.employeeIds.length})
                  </span>
                )}
              </Label>
              <div className="space-y-1 max-h-24 overflow-y-auto border rounded-md p-1.5">
                {employees.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No employees</p>
                ) : (
                  employees.map((employee) => (
                    <div key={employee.id} className="flex items-center space-x-1.5">
                      <Checkbox
                        id={`employee-${employee.id}`}
                        checked={filters.employeeIds.includes(employee.id)}
                        onCheckedChange={() => toggleEmployee(employee.id)}
                        className="h-3.5 w-3.5"
                      />
                      <label
                        htmlFor={`employee-${employee.id}`}
                        className="text-xs cursor-pointer truncate"
                      >
                        {employee.first_name || ''} {employee.last_name || ''}
                      </label>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Leave Type Filter */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">
                Leave Type
                {filters.leaveTypes.length > 0 && (
                  <span className="text-muted-foreground ml-1">
                    ({filters.leaveTypes.length})
                  </span>
                )}
              </Label>
              <div className="space-y-1 max-h-24 overflow-y-auto border rounded-md p-1.5">
                {availableLeaveTypes.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No leave types</p>
                ) : (
                  availableLeaveTypes.map((type) => (
                    <div key={type} className="flex items-center space-x-1.5">
                      <Checkbox
                        id={`type-${type}`}
                        checked={filters.leaveTypes.includes(type)}
                        onCheckedChange={() => toggleLeaveType(type)}
                        className="h-3.5 w-3.5"
                      />
                      <label
                        htmlFor={`type-${type}`}
                        className="text-xs cursor-pointer truncate"
                      >
                        {formatLeaveType(type)}
                      </label>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="p-3 border-t bg-muted/50">
          <Button
            onClick={handleApply}
            className="w-full h-8 text-sm"
            disabled={!hasPendingChanges}
          >
            <Check className="h-3.5 w-3.5 mr-1.5" />
            Apply Filters
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
